import React, { useState, useEffect,useCallback, useRef } from "react";
import { Container, Row, Col, Image } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "./index.css";
import {
  callApi,
  callSecondApi,
  generateMockups,
  callApiWithRateLimiting,
  callSecondApiWithRateLimiting,
  DevTools,
  useDevTools,
  uploadNewImage,
  fetchMultipleGeneratedImages,
  callApiBulk,
  extractColorPalette
} from "./ApiCaller";
import loadingImage from "./images/loading.svg";
import errorImage from "./images/error.png";
import MockupImages from "./mockupImages.js";
import ImageUploader from "./ImageUploader.js";
import ImageComponent from "./ImageComponent.js";
import MockupEditor from "./MockupEditor";
import { Fragment } from 'react'
import { Menu, Popover, Transition } from '@headlessui/react'
import { Bars3Icon, BellIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import DropdownMenu from './DropdownMenu';
import { useLocation } from 'react-router-dom';
import formConfig from './formConfig.json';
import { Modal } from 'flowbite-react';
import CustomSignupForm from './components/CustomSignupForm';
import ColorPalette from './ColorPalette';
import FlipBook from './components/FlipBook';

const safeResizeObserver = (callback) => {
  return new ResizeObserver((entries) => {
    window.requestAnimationFrame(() => {
      if (!Array.isArray(entries) || !entries.length) {
        return;
      }
      callback(entries);
    });
  });
};

function App() {
  const devTools = useDevTools();
  const location = useLocation();
  const [clientId, setClientId] = useState('default');
  const [clientMockups, setClientMockups] = useState('default');
  const [flipBookMode, setFlipBookMode] = useState(false);
  const [flipBookOrientation, setFlipBookOrientation] = useState('portrait');
  const [isLoading, setIsLoading] = useState(true);
  const [baseImagesLoaded, setBaseImagesLoaded] = useState(false);
  const [shouldGenerateMockups, setShouldGenerateMockups] = useState(false);
  const [autoGenerateTriggered, setAutoGenerateTriggered] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [totalTime, setTotalTime] = useState(null);
  const [isProcessComplete, setIsProcessComplete] = useState(false);
  const [pollInterval, setPollInterval] = useState(null);
  const [allImagesGenerated, setAllImagesGenerated] = useState(false);
  const [processingImages, setProcessingImages] = useState([]);
  const [pollStartTime, setPollStartTime] = useState(null);
  const [pollEndTime, setPollEndTime] = useState(null);
  const [isPolling, setIsPolling] = useState(false);

  // Add color palette state
  const [colorPalette, setColorPalette] = useState([]);
  const [isExtractingColors, setIsExtractingColors] = useState(false);

  // Add debug logging for color palette state changes
  useEffect(() => {
    console.log('🌈 App: colorPalette state changed:', colorPalette);
  }, [colorPalette]);

  useEffect(() => {
    console.log('🌈 App: isExtractingColors state changed:', isExtractingColors);
  }, [isExtractingColors]);

  useEffect(() => {
    console.log("App initial useEffect");
    const searchParams = new URLSearchParams(window.location.search);
    const client = searchParams.get('client');
    const placementImageParam = searchParams.get('placementImage');
    const mode = searchParams.get('mode');
    const orientation = searchParams.get('orientation');

    if (mode === 'flipbook') {
      setFlipBookMode(true);
      setFlipBookOrientation(orientation === 'landscape' ? 'landscape' : 'portrait');
    }

    console.log(`Retrieved client: ${client}, PlacementImage: ${placementImageParam}`);
    
    if (client) {
      setClientId(client);
      setClientMockups(client);
    } else {
      setClientId('default');
      setClientMockups('default');
    }

    if (placementImageParam) {
      setPlacementImage(placementImageParam);
      setUploadedImageUrl(placementImageParam);
      handleImageUploadComplete(29, placementImageParam); // Simulate image upload
    }

    const currentConfig = formConfig[client] || formConfig.default;
    if (currentConfig.autoGenerateMockups) {
      console.log("Setting up for auto-generating mockups based on formConfig");
      setShouldGenerateMockups(true);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    console.log("Auto-generate check useEffect");
    console.log("baseImagesLoaded:", baseImagesLoaded);
    console.log("shouldGenerateMockups:", shouldGenerateMockups);
    console.log("uploadedImageId:", uploadedImageId);
    console.log("uploadedImageUrl:", uploadedImageUrl);
    console.log("autoGenerateTriggered:", autoGenerateTriggered);

    if (baseImagesLoaded && shouldGenerateMockups && uploadedImageId && uploadedImageUrl && !autoGenerateTriggered) {
      console.log("All conditions met, auto-generating mockups...");
      generateAllMockups(uploadedImageId, uploadedImageUrl);
      setAutoGenerateTriggered(true);
    } else {
      console.log("Not all conditions met for auto-generating mockups");
    }
  }, [baseImagesLoaded, shouldGenerateMockups, uploadedImageId, uploadedImageUrl, autoGenerateTriggered]);

  const [filteredImages, setFilteredImages] = useState([]);
  const [previewSrc, setPreviewSrc] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState("");
  const [uploadedImageFile, setUploadedImageFile] = useState(null);
  const [selectedMockup, setSelectedMockup] = useState(null);
  const [placementImage, setPlacementImage] = useState("");
  const [selectedMockupImage, setSelectedMockupImage] = useState(null);
  const [imageUrls, setImageUrls] = useState({});
  const [imageLoading, setImageLoading] = useState({});
  const [isPreviewImage, setIsPreviewImage] = useState({});
  const [highQualityImageLoading, setHighQualityImageLoading] = useState({});
  const [imageId, setImageId] = useState(null);
  const [mockupImageUrl, setMockupImageUrl] = useState("");
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [inputChanged, setInputChanged] = useState(false);
  const [isGeneratingMockups, setIsGeneratingMockups] = useState(false);
  const [uploadedImageId, setUploadedImageId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [formLoaded, setFormLoaded] = useState(false);
  const formContainerRef = useRef(null);
  const [modalSize, setModalSize] = useState({ width: '100%', height: '615px' });
  const [highQualityImages, setHighQualityImages] = useState({});
  const [highQualityLoading, setHighQualityLoading] = useState({});

  // Add new state for the image preview modal
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState("");

  const [showLoadingNotification, setShowLoadingNotification] = useState(false);
  const [loadingNotificationTimeout, setLoadingNotificationTimeout] = useState(null);

  const updateFilteredImages = useCallback((newFilteredImages) => {
    setFilteredImages(prevFilteredImages => {
      if (JSON.stringify(newFilteredImages) !== JSON.stringify(prevFilteredImages)) {
        return newFilteredImages;
      }
      return prevFilteredImages;
    });
  }, []);

  const handleMockupImageSelection = (images) => {
    if (Array.isArray(images)) {
      setFilteredImages(images);
    } else {
      setSelectedMockup(images);
    }
  };

  const handleImageUploadComplete = useCallback(async (id, uri, shouldAutoGenerate = false) => {
    console.log("Image upload complete. ID:", id, "URI:", uri, "Should auto-generate:", shouldAutoGenerate);
    setMockupImageUrl(uri);
    setImageId(id);
    setUploadedImageId(id);
    setUploadedImageUrl(uri);
    setInitialDataLoaded(true);

    // Extract color palette from uploaded image (skip if static mode)
    const clientConfig = formConfig[clientId] || formConfig.default;
    const isStaticColorMode = clientConfig.colorPickerMode === 'static';

    if (id && !isStaticColorMode) {
      setIsExtractingColors(true);
      try {
        const colors = await extractColorPalette(id);
        setColorPalette(colors);
        console.log("Extracted color palette:", colors);
      } catch (error) {
        console.error("Failed to extract color palette:", error);
        setColorPalette([]);
      } finally {
        setIsExtractingColors(false);
      }
    } else if (isStaticColorMode) {
      console.log("Static color mode enabled, skipping color extraction");
      setColorPalette([]); // Clear any existing palette
    }

    // If shouldAutoGenerate is true, trigger mockup generation
    if (shouldAutoGenerate) {
      console.log("Auto-generating mockups after logo selection");
      generateAllMockups(id, uri);
    }
  }, []);

  const handleBaseImagesLoaded = useCallback(() => {
    console.log("Base images loaded callback triggered in App");
    setBaseImagesLoaded(true);
  }, []);

  const setEndTimeAndTotalTime = (devTools, startTime) => {
    if (devTools.showDevTools) {
      const endTime = new Date();
      devTools.setEndTime(endTime);
      if (startTime) {
        const totalTime = (endTime.getTime() - startTime.getTime()) / 1000; // in seconds
        devTools.setTotalTime(totalTime);
      } else {
        console.warn('Start time was not provided. Unable to calculate total time.');
        devTools.setTotalTime(null);
      }
    }
  };

  const generateAllMockups = async (overlayImageId, overlayImageUrl) => {
    console.log('⚪ App: generateAllMockups called (REGULAR - no color palette)');
    console.log("Generating all mockups");
    if (!overlayImageId) {
      console.log("No overlay image ID, showing alert");
      showAlert("An overlay image is required to generate mockups.");
      return;
    }

    setIsGeneratingMockups(true);
    setStartTime(new Date());
    setPollStartTime(null);
    setPollEndTime(null);
    setEndTime(null);
    setTotalTime(null);
    setIsProcessComplete(false);
    setAllImagesGenerated(false);
    setIsPolling(false);
    console.log("Resetting imageUrls and loading states");
    setImageUrls({});
    setHighQualityImages({});
    setImageLoading(prevLoading => {
      const newLoading = {};
      filteredImages.forEach(image => {
        newLoading[image.id] = true;
      });
      return newLoading;
    });

    try {
      const baseImageIds = filteredImages.map(image => image.id);
      setProcessingImages(baseImageIds);
      await generateMockups(
        baseImageIds,
        overlayImageId,
        overlayImageUrl,
        null,
        "",
        "",
        (newImages, state) => {
          console.log("Received new images:", newImages);
          console.log("State:", state);
          if (state === 'ready') {
            newImages.forEach(image => {
              const baseImageId = filteredImages.find(fi => fi.id === image.id)?.id;
              if (baseImageId) {
                console.log(`Updating image for baseImageId: ${baseImageId}, URL: ${image.uri}, isHighQuality: ${image.isHighQuality}`);
                setImageLoading(prev => ({ ...prev, [baseImageId]: false }));
                
                if (image.isHighQuality) {
                  setHighQualityImages(prev => {
                    console.log("Setting high quality image:", baseImageId, image.uri);
                    return { ...prev, [baseImageId]: image.uri };
                  });
                } else {
                  setImageUrls(prev => ({ ...prev, [baseImageId]: image.uri }));
                }
                setProcessingImages(prev => prev.filter(id => id !== baseImageId));
              } else {
                console.warn(`Could not find matching baseImageId for image:`, image);
              }
            });
          }
        },
        null,
        true,
        clientId,
        devTools
      );

      console.log("All mockup generations completed");
      setAllImagesGenerated(true);
    } catch (error) {
      console.error("Error generating mockups:", error);
      showAlert("An error occurred while generating mockups.");
      setIsProcessComplete(true);
    }
  };

  // New function to generate mockups with color palette
  const generateAllMockupsWithColors = async (paletteColors) => {
    console.log('🌈 App: generateAllMockupsWithColors called with paletteColors:', paletteColors);
    console.log('🌈 App: This should include palette data in render config');

    if (!uploadedImageId) {
      console.log("No overlay image ID, showing alert");
      showAlert("An overlay image is required to generate mockups.");
      return;
    }

    setIsGeneratingMockups(true);
    setStartTime(new Date());
    setPollStartTime(null);
    setPollEndTime(null);
    setEndTime(null);
    setTotalTime(null);
    setIsProcessComplete(false);
    setAllImagesGenerated(false);
    setIsPolling(false);
    console.log("Resetting imageUrls and loading states");
    setImageUrls({});
    setHighQualityImages({});
    setImageLoading(prevLoading => {
      const newLoading = {};
      filteredImages.forEach(image => {
        newLoading[image.id] = true;
      });
      return newLoading;
    });

    try {
      const baseImageIds = filteredImages.map(image => image.id);
      setProcessingImages(baseImageIds);

      // Get the client-specific configuration
      const clientConfig = formConfig[clientId] || formConfig.default;

      // Convert RGB array to hex color
      const rgbToHex = (rgb) => {
        const [r, g, b] = rgb;
        return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
      };

      // Create render config with palette data
      const renderConfigWithPalette = {
        data: {
          palette: {
            images: {
              placement: paletteColors
            }
          }
        }
      };

      // Check if colorSettings.colorMask is enabled in formConfig
      if (clientConfig.colorSettings && clientConfig.colorSettings.colorMask && paletteColors.length > 0) {
        // Add the first color as placementColorMask (as hex code)
        const firstColorHex = rgbToHex(paletteColors[0]);
        renderConfigWithPalette.placementColorMask = firstColorHex;
        console.log("🎨 App: Added placementColorMask to render config:", firstColorHex);
      }

      console.log("🌈 App: Created renderConfigWithPalette:", JSON.stringify(renderConfigWithPalette, null, 2));

      await generateMockups(
        baseImageIds,
        uploadedImageId,
        uploadedImageUrl,
        null,
        "",
        "",
        (newImages, state) => {
          console.log("Received new images:", newImages);
          console.log("State:", state);
          if (state === 'ready') {
            newImages.forEach(image => {
              const baseImageId = filteredImages.find(fi => fi.id === image.id)?.id;
              if (baseImageId) {
                console.log(`Updating image for baseImageId: ${baseImageId}, URL: ${image.uri}, isHighQuality: ${image.isHighQuality}`);
                setImageLoading(prev => ({ ...prev, [baseImageId]: false }));
                
                if (image.isHighQuality) {
                  setHighQualityImages(prev => {
                    console.log("Setting high quality image:", baseImageId, image.uri);
                    return { ...prev, [baseImageId]: image.uri };
                  });
                } else {
                  setImageUrls(prev => ({ ...prev, [baseImageId]: image.uri }));
                }
                setProcessingImages(prev => prev.filter(id => id !== baseImageId));
              } else {
                console.warn(`Could not find matching baseImageId for image:`, image);
              }
            });
          }
        },
        renderConfigWithPalette,
        true,
        clientId,
        devTools
      );

      console.log("All mockup generations with colors completed");
      setAllImagesGenerated(true);
    } catch (error) {
      console.error("Error generating mockups with colors:", error);
      showAlert("An error occurred while generating mockups with colors.");
      setIsProcessComplete(true);
    }
  };

  useEffect(() => {
    if (allImagesGenerated && !isPolling) {
      setPollStartTime(new Date());
      setIsPolling(true);
      startPolling();
    }
  }, [allImagesGenerated, isPolling]);

  const startPolling = () => {
    console.log("Starting polling for image loading");
    const interval = setInterval(() => {
      console.log("Polling: Processing images:", processingImages);
      console.log("Polling: Image loading states:", imageLoading);
      console.log("Polling: Image URLs:", imageUrls);
      console.log("Polling: High-quality images:", highQualityImages);

      const allImagesReady = processingImages.length === 0 && 
        filteredImages.every(image => {
          const isLoaded = !imageLoading[image.id];
          const hasLowQuality = imageUrls[image.id];
          const hasHighQuality = highQualityImages[image.id];
          console.log(`Image ${image.id}: loaded=${isLoaded}, lowQuality=${!!hasLowQuality}, highQuality=${!!hasHighQuality}`);
          return isLoaded && (hasLowQuality || hasHighQuality);
        });

      console.log("Polling: All images ready?", allImagesReady);

      if (allImagesReady) {
        console.log("All images are ready, stopping polling");
        clearInterval(interval);
        setIsGeneratingMockups(false);
        setIsProcessComplete(true);
        setPollEndTime(new Date());
        setEndTime(new Date());
        calculateTotalTime();
      }
    }, 1000); // Check every second

    setPollInterval(interval);
  };

  const calculateTotalTime = () => {
    if (startTime && pollStartTime && pollEndTime) {
      const initialProcessTime = pollStartTime.getTime() - startTime.getTime();
      const pollingTime = pollEndTime.getTime() - pollStartTime.getTime();
      const totalTime = initialProcessTime + pollingTime;
      setTotalTime(totalTime / 1000); // Convert to seconds
      console.log("Process complete. Initial process time:", initialProcessTime / 1000, "seconds");
      console.log("Polling time:", pollingTime / 1000, "seconds");
      console.log("Total time:", totalTime / 1000, "seconds");
    }
  };

  useEffect(() => {
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [pollInterval]);

  const [showMockupEditor, setShowMockupEditor] = useState(false);
  const setShowMockupEditorDebug = (value) => {
    setShowMockupEditor(value);
  }

  
  const [useMockupEditor, setUseMockupEditor] = useState(false);

  const [alertMessage, setAlertMessage] = useState("");
  const prepareSelectedMockup = (id, src) => {
    if (useMockupEditor) {
      setSelectedMockup({
        id,
        url: src,
        placementX: 1,
        placementY: 1,
        placementW: 333,
        placementH: 333,
      });
    }
  };

  const showAlert = (message) => {
    setAlertMessage(message);
    setTimeout(() => {
      setAlertMessage("");
    }, 3000);
  };

  const onImageClick = async (src, id) => {
    if (highQualityLoading[id]) {
      // Show a more noticeable notification instead of just an alert
      setShowLoadingNotification(true);
      
      // Clear any existing timeout
      if (loadingNotificationTimeout) {
        clearTimeout(loadingNotificationTimeout);
      }
      
      // Set a new timeout to hide the notification after 3 seconds
      const timeout = setTimeout(() => {
        setShowLoadingNotification(false);
      }, 3000);
      
      setLoadingNotificationTimeout(timeout);
      return;
    }

    if (highQualityImages[id]) {
      setPreviewSrc(highQualityImages[id]);
      setModalImageSrc(highQualityImages[id]);
      setIsPreviewOpen(true);
    } else if (imageUrls[id]) {
      // Show the notification for high-quality not ready yet
      setShowLoadingNotification(true);
      
      // Clear any existing timeout
      if (loadingNotificationTimeout) {
        clearTimeout(loadingNotificationTimeout);
      }
      
      // Set a new timeout to hide the notification after 3 seconds
      const timeout = setTimeout(() => {
        setShowLoadingNotification(false);
      }, 3000);
      
      setLoadingNotificationTimeout(timeout);
    } else {
      if (!uploadedImageId) {
        showAlert("Please upload an overlay image to generate the mockup.");
        return;
      }

      showAlert("Generating high-quality mockup. Please wait...");
      setHighQualityImageLoading(prev => ({ ...prev, [id]: true }));

      handleGenerateMockup(id, uploadedImageId, uploadedImageUrl, uploadedImageFile, null, "", (url) => {
        setLoading(false);
        setImageUrls(prevUrls => ({ ...prevUrls, [id]: url }));
        setPreviewSrc(url);
        setModalImageSrc(url); // Set the modal image source
        setIsPreviewOpen(true);
        setHighQualityImageLoading(prev => ({ ...prev, [id]: false }));
      });
    }
  };

  const handleGenerateMockup = useCallback((id, imageId, uploadedImageUrl, uploadedImageFile, largeInputValue, colorValue, callback) => {
    generateMockups(
      [id], // Wrap the id in an array since generateMockups now expects an array of ids
      imageId, 
      uploadedImageUrl, 
      uploadedImageFile, 
      largeInputValue, 
      colorValue, 
      (newImages, state) => {
        if (state === 'ready' && newImages.length > 0) {
          callback(newImages[0].uri);
        }
      },
      null,
      false,
      clientId,
      devTools
    );
  }, [clientId, devTools]);

  const clearPreview = () => {
    setPreviewSrc(null);
    setIsPreviewOpen(false);
  };

  const handleActionButtonClick = () => {
    setShowModal(true);
  };

  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setFormLoaded(false);
    setModalSize({ width: '100%', height: '615px' });
  }, []);

  const renderFormContent = () => {
    const formUrl = formConfig[clientId]?.formUrl || formConfig.default.formUrl;
    
    if (formUrl === 'custom') {
      return <CustomSignupForm onFormSubmit={() => {}} />;
    } else {
      return null; // We'll handle the iframe creation in the useEffect
    }
  };

  useEffect(() => {
    if (showModal && !formLoaded && formContainerRef.current) {
      console.log("Loading form for clientId:", clientId);
      const formUrl = formConfig[clientId]?.formUrl || formConfig.default.formUrl;
      console.log("Form URL:", formUrl);
      
      if (formUrl !== 'custom') {
        // Clear any existing content
        if (formContainerRef.current) {
          formContainerRef.current.innerHTML = '';
        }
        
        const f = document.createElement("iframe");
        f.src = formUrl;
        f.style.border = "none";
        f.style.height = "615px";
        f.style.width = "100%";
        f.style.transition = "all 0.5s ease";
        f.setAttribute("aria-label", 'Signup Form');

        if (formContainerRef.current) {
          formContainerRef.current.appendChild(f);
          console.log("Iframe appended to container");
          
          // Set formLoaded to true after appending the iframe
          setFormLoaded(true);
        }

        const handleMessage = (event) => {
          console.log("Received message:", event.data);
          const evntData = event.data;
          if (evntData && evntData.constructor === String) {
            const zf_ifrm_data = evntData.split("|");
            if (zf_ifrm_data.length === 2 || zf_ifrm_data.length === 3) {
              const zf_perma = zf_ifrm_data[0];
              const zf_ifrm_ht_nw = (parseInt(zf_ifrm_data[1], 10) + 15) + "px";
              requestAnimationFrame(() => {
                setModalSize(prevSize => ({ ...prevSize, height: zf_ifrm_ht_nw }));
              });
              if ((f.src).indexOf('formperma') > 0 && (f.src).indexOf(zf_perma) > 0) {
                f.style.height = zf_ifrm_ht_nw;
                console.log("Iframe height updated:", zf_ifrm_ht_nw);
              }
            }
          }
        };

        window.addEventListener('message', handleMessage, false);

        const resizeObserver = safeResizeObserver(([entry]) => {
          setModalSize({ width: '100%', height: `${entry.contentRect.height}px` });
        });

        if (formContainerRef.current) {
          resizeObserver.observe(formContainerRef.current);
        }

        return () => {
          window.removeEventListener('message', handleMessage);
          resizeObserver.disconnect();
        };
      } else {
        setFormLoaded(true);
      }
    }
  }, [showModal, formLoaded, clientId]);

  // Add this useEffect to log the devTools state
  useEffect(() => {
    console.log("DevTools state:", devTools);
  }, [devTools]);

  // Add a function to open the image modal
  const openImageModal = () => {
    if (previewSrc) {
      setModalImageSrc(previewSrc);
      setShowImageModal(true);
    }
  };

  // Clean up the timeout when component unmounts
  useEffect(() => {
    return () => {
      if (loadingNotificationTimeout) {
        clearTimeout(loadingNotificationTimeout);
      }
    };
  }, [loadingNotificationTimeout]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Find the modal container element and add or modify its style
  const modalStyle = {
    marginTop: 'max(20px, 2vh)', // Ensures at least 40px from the top
    maxHeight: 'calc(100vh - 80px)', // Adjusts max height to account for top and bottom margins
    overflow: 'auto', // Allows scrolling if content exceeds max height
  };

  // In the App component
  const handleUpdateMockup = (mockupId, newImageUrl) => {
    setImageUrls(prevUrls => ({ ...prevUrls, [mockupId]: newImageUrl }));
    setPreviewSrc(newImageUrl);
    setIsPreviewOpen(true);
  };

  if (flipBookMode) {
    return (
      <>
        {/* Hidden MockupImages for data loading */}
        <div style={{ position: 'absolute', visibility: 'hidden', height: 0, overflow: 'hidden' }}>
          {clientId && (
            <MockupImages
              onImageClick={onImageClick}
              selectedMockup={selectedMockup}
              loading={loading}
              noImage={!uploadedImageUrl && !uploadedImageFile}
              handleMockupImageSelection={handleMockupImageSelection}
              categories={formConfig[clientId]?.categories || ["All", "Featured"]}
              categoriesHide={true}
              imageUrls={imageUrls}
              imageLoading={imageLoading}
              highQualityImages={highQualityImages}
              clientMockups={clientMockups}
              updateFilteredImages={updateFilteredImages}
              resetInputChanged={() => setInputChanged(false)}
              clientId={clientId}
              uploadedImageUrl={uploadedImageUrl}
              onBaseImagesLoaded={handleBaseImagesLoaded}
            />
          )}
        </div>

        {/* Compact upload bar for flipbook mode */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          background: 'rgba(10,10,15,0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          padding: '8px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          {uploadedImageUrl && (
            <img src={uploadedImageUrl} alt="Logo" style={{ maxHeight: 28, maxWidth: 60, objectFit: 'contain', opacity: 0.8 }} />
          )}
          <div style={{ flex: 1 }}>
            <ImageUploader
              setUploadedImageUrl={setUploadedImageUrl}
              setUploadedImageFile={setUploadedImageFile}
              showAlert={showAlert}
              placementImage={placementImage}
              onImageUploadComplete={(id, uri) => handleImageUploadComplete(id, uri, false)}
              formConfig={{ ...(formConfig[clientId] || formConfig.default), showUploadBox: false }}
              clientId={clientId}
              generateAllMockups={generateAllMockups}
            />
          </div>
          <button
            onClick={() => generateAllMockups(uploadedImageId, uploadedImageUrl)}
            disabled={!uploadedImageId || isGeneratingMockups}
            style={{
              background: uploadedImageId && !isGeneratingMockups ? '#3b82f6' : '#444',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '6px 16px',
              fontSize: 12,
              fontWeight: 600,
              cursor: uploadedImageId && !isGeneratingMockups ? 'pointer' : 'default',
              letterSpacing: '0.5px',
              whiteSpace: 'nowrap',
            }}
          >
            Generate
          </button>
        </div>

        {alertMessage && (
          <div style={{ position: 'fixed', top: 50, width: '100%', zIndex: 1000 }} className="alert alert-info" role="alert">
            {alertMessage}
          </div>
        )}

        {/* Full-page FlipBook */}
        <div style={{ paddingTop: 50, height: '100vh', background: '#0a0a0f' }}>
          <FlipBook
            images={filteredImages}
            imageUrls={imageUrls}
            highQualityImages={highQualityImages}
            imageLoading={imageLoading}
            orientation={flipBookOrientation}
            onImageClick={onImageClick}
            uploadedImageUrl={uploadedImageUrl}
          />
        </div>

        {/* Image Preview Modal */}
        <Modal 
          show={showImageModal} 
          onClose={() => setShowImageModal(false)} 
          size="xl"
          position="center"
          dismissible={true}
        >
          <Modal.Header className="p-2 border-b">
            Mockup Image
          </Modal.Header>
          <Modal.Body className="p-0 flex justify-center items-center">
            {modalImageSrc && (
              <img 
                src={modalImageSrc} 
                alt="Mockup Preview" 
                className="max-w-full max-h-[80vh] object-contain"
              />
            )}
          </Modal.Body>
          <Modal.Footer className="flex justify-center">
            <button
              onClick={() => setShowImageModal(false)}
              type="button"
              className="bg-gray-200 hover:bg-gray-300 focus:ring-4 focus:ring-gray-300 focus:!ring-2 group flex h-min items-center justify-center text-center font-medium focus:z-10 rounded-lg text-gray-800 shadow-lg px-4 py-2"
            >
              Close
            </button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  return (
    <>
      <div className="min-h-full">
        <Popover as="header" className="pb-24">
          {({ open }) => (
            <>
              <div className="mx-auto max-w-3xl px-1 sm:px-6 lg:max-w-7xl lg:px-8">
              </div>

              <Transition.Root as={Fragment}>
                <div className="lg:hidden">
                  <Transition.Child
                    as={Fragment}
                    enter="duration-150 ease-out"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="duration-150 ease-in"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Popover.Overlay className="fixed inset-0 z-20 bg-black bg-opacity-25" />
                  </Transition.Child>

                  <Transition.Child
                    as={Fragment}
                    enter="duration-150 ease-out"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="duration-150 ease-in"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Popover.Panel
                      focus
                      className="absolute inset-x-0 top-0 z-30 mx-auto w-full max-w-3xl origin-top transform p-2 transition"
                    >
                      <div className="divide-y divide-gray-200 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                        <div className="pb-2 pt-3">
                          <div className="flex items-center justify-between px-4">
                            <div>
                              <img
                                className="h-8 w-auto"
                                src="https://tailwindui.com/img/logos/mark.svg?color=indigo&shade=600"
                                alt="Your Company"
                              />
                            </div>
                            <div className="-mr-2">
                              <Popover.Button className="inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
                                <span className="sr-only">Close menu</span>
                                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                              </Popover.Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Popover.Panel>
                  </Transition.Child>
                </div>
              </Transition.Root>
            </>
          )}
        </Popover>
        <main className="-mt-24 pb-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h1 className="sr-only">Page title</h1>
            <div className={`grid grid-cols-1 ${isPreviewOpen ? 'lg:grid-cols-3' : 'lg:grid-cols-1'} gap-4 lg:gap-8`}>
              {/* Upload Image Section */}
              <div className={`${isPreviewOpen ? 'lg:col-span-3' : 'lg:col-span-1'} order-1 pt-2`}>
                <section aria-labelledby="upload-preview-section">
                  <div className="rounded-lg bg-white shadow">
                    <DropdownMenu clientId={clientId} clientMockups={clientMockups} />
                    <div className="px-4 sm:px-6">
                      <ImageUploader
                        setUploadedImageUrl={setUploadedImageUrl}
                        setUploadedImageFile={setUploadedImageFile}
                        showAlert={showAlert}
                        placementImage={placementImage}
                        onImageUploadComplete={(id, uri) => handleImageUploadComplete(id, uri, false)}
                        formConfig={formConfig[clientId] || formConfig.default}
                        clientId={clientId}
                        generateAllMockups={generateAllMockups}
                      />
                      {alertMessage && (
                        <div style={{ position: 'fixed', top: 0, width: '100%', zIndex: 1000 }} className="alert alert-info" role="alert">
                          {alertMessage}
                        </div>
                      )}
                      {/* Generate Mockups button */}
                      <div className="my-4 flex justify-center pb-2">
                        <button
                          onClick={() => generateAllMockups(uploadedImageId, uploadedImageUrl)}
                          type="button"
                          className="bg-blue-600 hover:bg-green-700 focus:ring-4 focus:ring-green-300 disabled:bg-gray-600 disabled:hover:bg-gray-600 dark:bg-green-500 dark:hover:bg-green-600 dark:focus:ring-green-800 focus:!ring-2 group flex h-min items-center justify-center text-center font-medium focus:z-10 rounded-lg text-white shadow-lg transform transition duration-150 hover:scale-105"
                          disabled={!uploadedImageId || isGeneratingMockups}
                        >
                          <span className="flex items-center rounded-md text-sm px-4 py-2">
                            Generate Mockups
                          </span>
                        </button>
                      </div>

                      {/* Color Palette Section */}
                      {(() => {
                        const clientConfig = formConfig[clientId] || formConfig.default;
                        const isStaticMode = clientConfig.colorPickerMode === 'static';
                        const shouldShowPalette = (isStaticMode || colorPalette.length > 0 || isExtractingColors) && (clientConfig.showColorPalette === true);
                        console.log('🌈 App: Color palette render check:', {
                          colorPaletteLength: colorPalette.length,
                          isExtractingColors,
                          clientId,
                          showColorPalette: clientConfig.showColorPalette,
                          isStaticMode,
                          shouldShowPalette
                        });
                        return shouldShowPalette;
                      })() && (
                        <div className="my-4 px-4 py-3 bg-gray-50 rounded-lg">
                          <ColorPalette
                            colors={colorPalette}
                            isLoading={isExtractingColors}
                            onGenerateWithColors={generateAllMockupsWithColors}
                            colorPickerMode={formConfig[clientId]?.colorPickerMode || 'dynamic'}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              </div>

              {/* Mockup Preview Section */}
              {isPreviewOpen && (
                <div className="lg:col-span-1 order-2 px-0">
                  <section aria-labelledby="mockup-preview-section">
                    <div className="rounded-lg bg-white shadow-lg p-6">
                      <button
                        onClick={clearPreview}
                        className="float-right bg-gray-100 p-1 rounded-full text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                      <div className="preview-container">
                        <h3 className="mb-4 text-center text-3xl font-extrabold leading-none tracking-tight text-gray-900 md:text-4xl lg:text-5xl dark:text-white">
                          Mockup Preview
                        </h3>
                        {/* Add onClick handler to the preview image container */}
                        <div onClick={openImageModal} className="cursor-pointer">
                          <ImageComponent
                            previewSrc={previewSrc}
                            loading={loading}
                            loadingImage={loadingImage}
                            uploadedImageUrl={uploadedImageUrl}
                            clientId={clientId}
                          />
                        </div>
                        {formConfig[clientId]?.showActionButton && (
                          <div className="mt-6 flex justify-center">
                            <button
                              onClick={handleActionButtonClick}
                              type="button"
                              className="bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 focus:!ring-2 group flex h-min items-center justify-center text-center font-medium focus:z-10 rounded-lg text-white shadow-lg transform transition duration-150 hover:scale-105"
                            >
                              <span className="flex items-center rounded-md text-sm px-4 py-2">
                                {formConfig[clientId]?.actionButtonText || 'Sign Up'}
                              </span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </section>
                </div>
              )}

              {/* Mockup Images Section */}
              <div className={`${isPreviewOpen ? 'lg:col-span-2' : 'lg:col-span-1'} order-3`}>
                <section aria-labelledby="mockup-images-section">
                  <div className="rounded-lg bg-white shadow">
                    <div className="px-4 sm:px-6">
                      {clientId && (
                        <MockupImages
                          onImageClick={onImageClick}
                          selectedMockup={selectedMockup}
                          loading={loading}
                          noImage={!uploadedImageUrl && !uploadedImageFile}
                          handleMockupImageSelection={handleMockupImageSelection}
                          categories={formConfig[clientId]?.categories || ["All", "Featured"]}
                          categoriesHide={formConfig[clientId]?.categoriesHide || false}
                          imageUrls={imageUrls}
                          imageLoading={imageLoading}
                          highQualityImages={highQualityImages}
                          clientMockups={clientMockups}
                          updateFilteredImages={updateFilteredImages}
                          resetInputChanged={() => setInputChanged(false)}
                          clientId={clientId}
                          uploadedImageUrl={uploadedImageUrl}
                          onBaseImagesLoaded={handleBaseImagesLoaded}
                        />
                      )}
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </main>
        <DevTools 
          devTools={devTools} 
          startTime={startTime}
          pollStartTime={pollStartTime}
          pollEndTime={pollEndTime}
          endTime={endTime}
          totalTime={totalTime}
          isProcessComplete={isProcessComplete}
        />

        <Modal 
          show={showModal} 
          onClose={handleModalClose} 
          size="lg"
          position="top-center"
          className="!mt-0"
          dismissible={true}
        >
          <Modal.Header className="p-2 border-b">
          </Modal.Header>
          <Modal.Body style={modalSize}>
            {!formLoaded && <div className="text-center">Loading...</div>}
            <div ref={formContainerRef} style={modalSize}>
              {formLoaded && renderFormContent()}
            </div>
          </Modal.Body>
        </Modal>

        {/* Image Preview Modal */}
        <Modal 
          show={showImageModal} 
          onClose={() => setShowImageModal(false)} 
          size="xl"
          position="center"
          dismissible={true}
        >
          <Modal.Header className="p-2 border-b">
            Mockup Image
          </Modal.Header>
          <Modal.Body className="p-0 flex justify-center items-center">
            {modalImageSrc && (
              <img 
                src={modalImageSrc} 
                alt="Mockup Preview" 
                className="max-w-full max-h-[80vh] object-contain"
              />
            )}
          </Modal.Body>
          <Modal.Footer className="flex justify-center">
            <button
              onClick={() => setShowImageModal(false)}
              type="button"
              className="bg-gray-200 hover:bg-gray-300 focus:ring-4 focus:ring-gray-300 focus:!ring-2 group flex h-min items-center justify-center text-center font-medium focus:z-10 rounded-lg text-gray-800 shadow-lg px-4 py-2"
            >
              Close
            </button>
          </Modal.Footer>
        </Modal>

        {/* Loading Notification */}
        {showLoadingNotification && (
          <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-sm mx-auto transform transition-all animate-bounce-once pointer-events-auto">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    High-quality image loading
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Please wait while we prepare the high-quality version of this mockup.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <style>
        {`
      [data-testid="modal"] > div {
  margin-top: ${formConfig[clientId]?.marginTop || 200}px!important;
  transform: none !important;
}
        
        /* Remove zoom effect from preview image */
        .preview-container img {
          transition: none !important;
          transform: none !important;
        }
        .preview-container img:hover {
          transform: none !important;
        }
        
        /* Animation for the loading notification */
        @keyframes bounce-once {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .animate-bounce-once {
          animation: bounce-once 1s ease-in-out;
        }
        `}
      </style>
    </>
  )
}

export default App;